import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useServices } from '../ServiceContext'
import type { PageInput, ListPagesParams, PageKind } from '../types'

// ─── Query keys ───────────────────────────────────────────────────────────────

export const pageKeys = {
  all: ['pages'] as const,
  list: (params?: ListPagesParams) => ['pages', 'list', params] as const,
  detail: (id: string) => ['pages', 'detail', id] as const,
  live: ['pages', 'live'] as const,
  liveByKindSlug: (kind: string, slug: string) => ['pages', 'live', kind, slug] as const,
  liveEditions: ['pages', 'live', 'editions'] as const,
}

// ─── Queries ──────────────────────────────────────────────────────────────────

export function usePages(params?: ListPagesParams) {
  const { pageService } = useServices()
  return useQuery({
    queryKey: pageKeys.list(params),
    queryFn: () => pageService.list(params),
  })
}

export function usePage(id: string) {
  const { pageService } = useServices()
  return useQuery({
    queryKey: pageKeys.detail(id),
    queryFn: () => pageService.get(id),
    enabled: Boolean(id),
  })
}

export function useLivePage() {
  const { pageService } = useServices()
  // Explicitly request kind=front-page so the query is independent of any
  // other "live" page the backend might return from the untyped /pages/live
  // endpoint (e.g. a special edition that was recently published).
  const params: ListPagesParams = { kind: 'front-page', status: 'live' }
  return useQuery({
    queryKey: pageKeys.list(params),
    queryFn: () => pageService.list(params),
    select: (pages) => pages[0],
    staleTime: 60_000,
  })
}

// Fetches all live pages of a given kind (one request shared by all callers
// with the same kind), then selects the page matching `slug` client-side.
// Falls back gracefully (data=undefined, isError=false) when the slug isn't
// found, avoiding extra round-trips to non-existent dedicated endpoints.
export function useLivePageByKindSlug(kind: string, slug: string) {
  const { pageService } = useServices()
  const params: ListPagesParams = { kind: kind as PageKind, status: 'live' }
  return useQuery({
    queryKey: pageKeys.list(params),
    queryFn: () => pageService.list(params),
    select: (pages) => pages.find((p) => p.slug === slug),
    enabled: Boolean(kind) && Boolean(slug),
    staleTime: 60_000,
  })
}

export function useLiveEditions() {
  const { pageService } = useServices()
  const params: ListPagesParams = { kind: 'edition', status: 'live' }
  return useQuery({
    queryKey: pageKeys.list(params),
    queryFn: () => pageService.list(params),
    staleTime: 60_000,
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status && status >= 400 && status < 500) return false
      return failureCount < 2
    },
  })
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export function useCreatePage() {
  const { pageService } = useServices()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: PageInput) => pageService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pageKeys.all })
    },
  })
}

export function useUpdatePage(id: string) {
  const { pageService } = useServices()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: PageInput) => pageService.update(id, input),
    onSuccess: (updated) => {
      queryClient.setQueryData(pageKeys.detail(id), updated)
      queryClient.invalidateQueries({ queryKey: pageKeys.all })
    },
  })
}

export function usePublishPage() {
  const { pageService } = useServices()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => pageService.publish(id),
    onSuccess: () => {
      // Publish can affect multiple pages (the newly live one + previously live one archived)
      queryClient.invalidateQueries({ queryKey: pageKeys.all })
    },
  })
}

export function useSchedulePage() {
  const { pageService } = useServices()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, scheduledFor }: { id: string; scheduledFor: string }) =>
      pageService.schedule(id, scheduledFor),
    onSuccess: (_updated, { id }) => {
      queryClient.invalidateQueries({ queryKey: pageKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: pageKeys.all })
    },
  })
}

export function useArchivePage() {
  const { pageService } = useServices()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => pageService.archive(id),
    onSuccess: (_updated, id) => {
      queryClient.invalidateQueries({ queryKey: pageKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: pageKeys.all })
    },
  })
}

export function useDeletePage() {
  const { pageService } = useServices()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => pageService.delete(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: pageKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: pageKeys.all })
    },
  })
}
